import React, { useState, useEffect, useCallback } from "react";
import {
  Mail,
  Github,
  Linkedin,
  ArrowUp,
  X,
  Menu,
  Phone,
  ExternalLink,
  ChevronDown,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ScrollReveal } from "./components/reactbits";
import TerminalWindow from "./components/TerminalWindow";
import Typewriter from "./components/Typewriter";
import PixelDino from "./components/PixelDino";

// --- STATIC DATA ---
const portfolioData = {
  name: "Aaryan S. Rojesara",
  user: "aaryan",
  host: "portfolio",
  roles: [
    "Full Stack Developer",
    "IoT Engineer",
    "AI / LLM Integrator",
    "Mobile App Developer",
  ],
  bio: "Results-driven Full Stack Developer and IoT Engineer building scalable web & mobile apps and integrating AI into production systems. Currently at Zuture, building IoT solutions and managing MCP servers that connect AI/LLM models with internal tools and automation workflows.",
  contact: {
    email: "aryan.rojesara@gmail.com",
    phone: "+91 9510123175",
    github: "https://github.com/Rojesara-Aaryan/",
    linkedin: "https://www.linkedin.com/in/aaryan-rojesara/",
  },
  projects: [
    {
      id: 9,
      title: "Orbit — AI Personal Assistant",
      category: "Privacy-First Hybrid AI Assistant @ Zuture",
      year: "2026",
      image: "https://placehold.co/600x400/000000/ffffff?text=Orbit",
      tech: ["Python", "FastAPI", "React", "Ollama", "PostgreSQL", "Redis", "LangChain"],
      description:
        "An AI personal assistant for founders & everyday users built on a privacy-first hybrid architecture.",
      details:
        "Flagship product built at Zuture (MSc Sem-VI project). Every request passes through an on-device Privacy Classifier (<50ms): personal data (emails, calendar, meetings, business metrics) is processed entirely on-device via a local LLM through Ollama, while general queries route to the user's chosen cloud provider (Claude, GPT-4, Gemini, Groq, Mistral) with zero personal data sent. Backend in Python/FastAPI with Celery + Redis async jobs and PostgreSQL (row-level security); React + Tailwind frontend; local Whisper transcription for Meeting Intelligence; Dockerized with GitHub Actions CI/CD.",
      highlights: [
        "On-device Privacy Classifier routes every request in under 50ms",
        "Personal data runs locally via Ollama; general queries route to Claude / GPT-4 / Gemini / Groq / Mistral",
        "Local Whisper transcription with auto Minutes-of-Meeting & action items",
        "FastAPI + Celery/Redis backend, PostgreSQL row-level security, React + Tailwind frontend",
      ],
      liveUrl: "#",
      repoUrl: "#",
    },
    {
      id: 1,
      title: "Vivienne Jewel's",
      category: "AI-Powered E-Commerce Mobile App",
      year: "2025",
      image: "vivienne.jpg",
      tech: ["Flutter", "PHP", "Django", "MySQL", "AI/ML", "NLP"],
      description:
        "A mobile app redefining online jewelry sales with AI-powered recommendations.",
      details:
        "Developed a Flutter mobile app with PHP APIs, a Django admin panel, and a MySQL database secured with OTP authentication. Implemented AI/ML models for personalized product recommendations based on user behavior and trends.",
      highlights: [
        "Cross-platform Flutter mobile app with PHP REST APIs",
        "Django admin panel + MySQL database with OTP authentication",
        "AI/ML personalized product recommendations",
        "NLP-driven search and user-behavior tracking",
      ],
      liveUrl: "#",
      repoUrl: "#",
    },
    {
      id: 2,
      title: "Kisan Mitra",
      category: "Agricultural Equipment Rental Platform",
      year: "2023",
      image: "kisanmitra.png",
      tech: ["Python", "Flask", "HTML/CSS", "Bootstrap", "SQL"],
      description:
        "A web portal for farmers offering equipment rentals and government scheme info.",
      details:
        "Developed with Flask, HTML, CSS, Bootstrap, and SQL. Features an equipment rental marketplace and an information section for government schemes.",
      highlights: [
        "Equipment rental marketplace for farmers",
        "Government schemes information section",
        "Built with Python Flask, Bootstrap, and SQL",
        "Responsive, accessible web platform",
      ],
      liveUrl: "#",
      repoUrl: "#",
    },
    {
      id: 3,
      title: "Automated Weed Cutter",
      category: "IoT & Agricultural Automation",
      year: "2022",
      image: "WeedCutter.jpg",
      tech: ["Arduino UNO", "Bluetooth", "Mobile App", "IoT"],
      description:
        "An Arduino-based weed-cutting system controlled via a mobile app.",
      details:
        "Integrated Arduino UNO with a mobile app via Bluetooth for remote weed-cutting operations, enabling farmers to manage operations from a smartphone.",
      highlights: [
        "Arduino UNO controller with Bluetooth connectivity",
        "Companion mobile app for remote control",
        "Hands-free field operation for farmers",
        "IoT hardware integration and embedded firmware",
      ],
      liveUrl: "#",
      repoUrl: "#",
    },
    {
      id: 4,
      title: "Tabelo",
      category: "Dairy Delivery & Scheduling App",
      year: "2023",
      image: "Tabelo.jpg",
      tech: ["Flutter", "Dart", "Django", "Python", "SQLite"],
      description:
        "A geolocation app connecting local dairy (tabela) owners with nearby residents to buy, pay for, and schedule daily milk.",
      details:
        "An app built for tabela (dairy) owners and the residents around them. Using geolocation, nearby users discover their local dairy, order and pay for milk directly in the app, and schedule recurring daily milk delivery timings. Built with a Python/Django backend, a Flutter (Dart) mobile app, and a SQLite database.",
      highlights: [
        "Geolocation discovery of nearby tabelas (dairies)",
        "In-app milk ordering and payments",
        "Recurring daily milk delivery scheduling",
        "Django backend, Flutter app, SQLite database",
      ],
      liveUrl: "#",
      repoUrl: "#",
    },
    {
      id: 5,
      title: "Jarvis Voice Assistant",
      category: "AI-Powered Voice Automation",
      year: "2024",
      image: "jarvis.jpeg",
      tech: ["Python", "NLP", "AI/ML"],
      description:
        "A Python voice assistant using NLP and ML for voice command recognition.",
      details:
        "Engineered a Python voice assistant using machine learning and NLP. Automates task execution including app launching, web browsing, and email management via speech.",
      highlights: [
        "Wake-word voice activation",
        "NLP-based natural language command recognition",
        "Automates app launching, web browsing & email",
        "Built in Python with machine learning",
      ],
      liveUrl: "#",
      repoUrl: "#",
    },
    {
      id: 6,
      title: "Gold Loan Management System",
      category: "Full-Stack Financial Services Platform",
      year: "2024",
      image: "https://placehold.co/600x400/ffffff/000000?text=Gold+Loan",
      tech: [".NET Core", "React", "SQL"],
      description:
        "A full-stack gold loan system handling the complete loan lifecycle.",
      details:
        "Developed with a .NET backend API, a React.js frontend, and a SQL database. Implemented end-to-end loan lifecycle management and automated report generation for analytics and business intelligence.",
      highlights: [
        "End-to-end gold loan lifecycle management",
        ".NET backend API with a React.js frontend",
        "SQL relational database",
        "Automated report generation for analytics & BI",
      ],
      liveUrl: "#",
      repoUrl: "#",
    },
    {
      id: 7,
      title: "Portfolio Website",
      category: "Personal Portfolio (this site)",
      year: "2025",
      image: "Portfolio.png",
      tech: ["React", "Tailwind CSS", "JavaScript"],
      description: "A minimalist terminal-themed portfolio.",
      details:
        "Built with React and Tailwind CSS with a focus on a clean, responsive, black & white terminal aesthetic.",
      highlights: [
        "Minimalist black & white Mac-terminal design",
        "Typed terminal hero with animated scroll reveals",
        "Fully responsive React + Tailwind CSS build",
        "Interactive project showcase with detail modals",
      ],
      liveUrl: "#",
      repoUrl: "#",
    },
    {
      id: 8,
      title: "Zuture - App Figma Design",
      category: "Mobile App UI/UX Design",
      year: "2025",
      image: "Zuture-app-figma.png",
      tech: ["Figma"],
      description: "A Figma design and prototype for the Zuture app.",
      details: "A complete Figma design and interactive prototype for the Zuture mobile application.",
      highlights: [
        "Complete mobile app UI/UX design",
        "Interactive, clickable prototype",
        "Reusable design system & components",
        "Designed end-to-end in Figma",
      ],
      liveUrl: "#",
      repoUrl: "#",
    },
    {
      id: 10,
      title: "KJ & Sons (Botadwala)",
      category: "Wholesale Jewellery Business Website",
      year: "2025",
      image: "kjsons.png",
      tech: ["React", "JavaScript", "Responsive Web"],
      freelance: true,
      description:
        "A business website for KJ & Sons, a wholesale antique-gold & CZ diamond jewellery supplier.",
      details:
        "Designed and developed the official website for KJ & Sons Pvt. Ltd. (Botadwala) — a wholesale jewellery distributor operating since 1994, specialising in antique gold and CZ diamond pieces, with showrooms in Manekchowk (Ahmedabad) and Botad, Gujarat. Built with React.js as a fast, responsive, brand-focused site for the jewellery trade.",
      highlights: [
        "Brand website for a wholesale jeweller (est. 1994)",
        "Antique gold & CZ diamond catalogue presentation",
        "Showroom presence: Manekchowk (Ahmedabad) & Botad",
        "Fast, responsive React.js build",
      ],
      liveUrl: "https://www.kjsons.com/",
      repoUrl: "#",
    },
    {
      id: 11,
      title: "RM.NM Architects",
      category: "Architecture Studio Website",
      year: "2025",
      image: "rcmnrm.png",
      tech: ["React", "JavaScript", "Responsive Web"],
      freelance: true,
      description:
        "A portfolio website for an architecture & design studio based in Bhavnagar, Gujarat.",
      details:
        "Designed and developed the website for RM.NM Architects, an architecture and design firm in Bhavnagar, Gujarat — presenting their built work and design philosophy through a clean, image-led layout.",
      highlights: [
        "Image-led architectural portfolio showcase",
        "Clean, minimal design-focused layout",
        "Fully responsive across devices",
        "Built with React.js",
      ],
      liveUrl: "https://rcmnrm.in/",
      repoUrl: "#",
    },
    {
      id: 12,
      title: "Riti Rasm",
      category: "Arts & Culture Events Platform",
      year: "2025",
      image: "ritirasm.png",
      tech: ["React", "JavaScript", "Responsive Web"],
      freelance: true,
      description:
        "A creative platform for entertainment, events, art and culture — where heritage meets the stage.",
      details:
        "Developed the website for Riti Rasm, a creative platform spanning art, drama, films, live shows and artist management. Sections cover event planning, artist management, a gallery, and cultural programming, presenting 'where heritage meets the stage.'",
      highlights: [
        "Showcases films, live shows & cultural events",
        "Artist management & event-planning sections",
        "Gallery and 'Art & Culture' content",
        "Responsive React.js build",
      ],
      liveUrl: "https://ritirasm.com/",
      repoUrl: "#",
    },
    {
      id: 13,
      title: "KS1 Technologies",
      category: "Software & AI Studio Website",
      year: "2026",
      image: "ks1technologies.png",
      tech: ["React", "JavaScript", "AI Chatbot"],
      freelance: true,
      description:
        "A marketing site for a software & AI studio building apps, websites, and manufacturing AI.",
      details:
        "Built the website for KS1 Technologies, an Ahmedabad-based software & AI studio offering app development, web development, and manufacturing-AI solutions. Includes an integrated 'KS1 Assistant' AI chat widget that helps visitors find the right solution for their business.",
      highlights: [
        "Marketing site for an app / web / AI studio",
        "Integrated 'KS1 Assistant' AI chat widget",
        "Positioned around manufacturing-AI solutions",
        "Modern, responsive React.js build",
      ],
      liveUrl: "https://ks1technologies.in/",
      repoUrl: "#",
    },
    {
      id: 14,
      title: "JwelBook",
      category: "AI Jewellery Catalogue & Ordering Platform",
      year: "2026",
      image: "https://placehold.co/600x400/000000/ffffff?text=JwelBook",
      tech: ["React Native", "FastAPI", "Python", "MongoDB", "AI/ML"],
      freelance: true,
      description:
        "A mobile-first platform that auto-sorts bulk jewellery photos into category folders using computer vision, with AI renders and WhatsApp ordering.",
      details:
        "An AI-powered jewellery catalogue & ordering platform for retailers, wholesalers and showrooms. Users drop in a bulk set of mixed product photos and computer-vision models automatically segregate them into category folders (chains, rings, bangles, necklaces, earrings…). It adds AI image generation for model-worn/styled renders, a full-screen slideshow mode for showroom display, and one-tap order creation that dispatches a formatted order over WhatsApp. Built as a React Native app on a Python/FastAPI microservices backend with MongoDB, object storage, a vector index, and an asynchronous GPU worker pipeline.",
      highlights: [
        "AI catalogue segregation — auto-sorts bulk photos into category folders (computer vision)",
        "AI image generation for model-worn / styled product renders",
        "Full-screen slideshow mode for showroom presentations",
        "One-tap order-from-image dispatched via the WhatsApp Business API",
      ],
      liveUrl: "#",
      repoUrl: "#",
    },
  ],
  skillGroups: [
    {
      title: "frameworks",
      skills: [
        "Python",
        "Django",
        "Flutter",
        ".NET Core",
        "React",
        "Node.js",
        "PHP",
        "Bootstrap",
        "Tailwind CSS",
        "JavaScript",
        "HTML5",
        "CSS3",
      ],
    },
    { title: "databases", skills: ["MySQL", "SQL Server"] },
    { title: "ai_ml", skills: ["AI/ML", "NLP", "MCP Servers", "R"] },
    { title: "iot", skills: ["Arduino", "Sensor Networks", "Firmware", "Bluetooth"] },
    { title: "tools", skills: ["Git", "Figma", "Excel"] },
  ],
  experience: [
    {
      role: "Junior Software Engineer (IoT)",
      company: "Zuture",
      period: "Dec 2025 - Present",
      isCurrent: true,
      description:
        "Developing IoT solutions for smart device connectivity and firmware optimization, and integrating sensor networks with cloud platforms. Set up and manage MCP servers, building custom tools and automation workflows that connect AI/LLM models with internal tools and data sources.",
    },
    {
      role: ".NET Developer Intern",
      company: "Appegic Technology",
      period: "Jun 2025 - Nov 2025",
      description:
        "Developed enterprise applications using the .NET Framework and C# with a focus on scalability and performance. Optimized database queries and implemented SDLC best practices.",
    },
    {
      role: "Technical Project Manager Intern",
      company: "PRAGYA LIMS",
      period: "Jun 2025 - Nov 2025",
      description:
        "Coordinated technical projects from conception to deployment using agile methodologies. Managed stakeholder communications and translated business requirements into technical specifications.",
    },
    {
      role: "Human Resource Intern",
      company: "Skyline Hire LTD",
      period: "Mar 2024 - Jul 2024",
      description:
        "Supported recruitment including candidate screening, interview coordination, and onboarding. Maintained employee databases and HR documentation with attention to confidentiality.",
    },
  ],
  education: [
    {
      degree: "Integrated M.Sc. in Information Technology (IMSc IT)",
      institution: "GLS University, Ahmedabad",
      period: "2021 - 2026 (Completed)",
      detail:
        "Integrated 5-year program with a strong foundation across full-stack development, databases, and emerging technologies.",
      sub: [
        "B.Sc. IT — 7.0 CGPA (2021 - 2024)",
        "M.Sc. IT — 9.1 CGPA (2024 - 2026)",
      ],
    },
    {
      degree: "Higher Secondary Education",
      institution: "R.A. Kalathiya Vidhyabhavan",
      period: "2019 - 2021",
      detail: "Commerce stream.",
    },
  ],
  // LinkedIn can't be auto-fetched (login wall), so posts are added manually.
  // To link a specific post: on LinkedIn open the post → ··· → "Copy link to
  // post" and replace `url` below.
  blog: [
    {
      title: "How I Built an AI-Powered Book Recommendation System — ShelfBot",
      category: "Project Breakdown",
      readTime: "5 min",
      blurb:
        "Building an AI library recommender — content-based + collaborative filtering with a feedback loop, and why clean data beat a fancy algorithm.",
      tags: ["AI/ML", "React", "Django"],
      url: "https://www.linkedin.com/in/aaryan-rojesara/",
    },
    {
      title: "What Coding Taught Me About Managing Projects",
      category: "Career & Leadership",
      readTime: "4 min",
      blurb:
        "Debugging is risk management, refactoring is retrospectives — the developer instincts that translate directly into project management.",
      tags: ["Project Mgmt", "Career", "Agile"],
      url: "https://www.linkedin.com/in/aaryan-rojesara/",
    },
    {
      title: "AI in 2026 — What's Real, What's Hype, and What's Coming",
      category: "AI & Technology",
      readTime: "5 min",
      blurb:
        "An honest take from someone who builds AI products: what AI genuinely does well today, what it still can't do, and where it's heading.",
      tags: ["AI", "Opinion", "Tech"],
      url: "https://www.linkedin.com/in/aaryan-rojesara/",
    },
    {
      title: "The 8-Year Experience Problem in Technical Project Management",
      category: "Career & Industry",
      readTime: "4 min",
      blurb:
        "Why 'minimum 8 years' filters out the people who could be exceptional — and what I'm doing with 1 year instead of waiting.",
      tags: ["TPM", "Career", "Hiring"],
      url: "https://www.linkedin.com/in/aaryan-rojesara/",
    },
    {
      title: "5 Agile Mistakes I Made and What I Learned From Them",
      category: "Project Management",
      readTime: "5 min",
      blurb:
        "Treating sprints as promises, skipping retros on good sprints, owning uncontrolled dependencies — five real Agile mistakes and their lessons.",
      tags: ["Agile", "Scrum", "Project Mgmt"],
      url: "https://www.linkedin.com/in/aaryan-rojesara/",
    },
    {
      title: "How I Think About Technical Project Management as a Developer",
      category: "Career & Leadership",
      readTime: "4 min",
      blurb:
        "A TPM sits between business and engineering — why a developer background is an unfair advantage at that translation layer.",
      tags: ["TPM", "Career", "Leadership"],
      url: "https://www.linkedin.com/in/aaryan-rojesara/",
    },
    {
      title: "Why Focus is the Most Underrated Skill in Tech",
      category: "Productivity",
      readTime: "4 min",
      blurb:
        "We have endless productivity tools yet less deep focus than ever. The attention-economy problem — and what actually works.",
      tags: ["Productivity", "Focus", "Deep Work"],
      url: "https://www.linkedin.com/in/aaryan-rojesara/",
    },
    {
      title: "From Developer to Technical Project Manager — My Honest Journey",
      category: "Career",
      readTime: "5 min",
      blurb:
        "Not a success story — a story in progress. Why I'm moving from building Django/Flutter/IoT toward technical project management.",
      tags: ["Career", "TPM", "Growth"],
      url: "https://www.linkedin.com/in/aaryan-rojesara/",
    },
    {
      title: "Building IoT Projects — Connecting Hardware to Software",
      category: "Technical",
      readTime: "5 min",
      blurb:
        "When code has physical consequences: lessons from the Arduino weed-cutting robot — power, integration bugs, and embedded reliability.",
      tags: ["IoT", "Arduino", "Embedded"],
      url: "https://www.linkedin.com/in/aaryan-rojesara/",
    },
    {
      title: "7 Things I Wish I Knew Before Starting My First Real Project",
      category: "Career & Project Management",
      readTime: "5 min",
      blurb:
        "Requirements will change, communication is 80% of the job, everyone defines 'done' differently — hard-won lessons from a first real project.",
      tags: ["Project Mgmt", "Lessons", "Career"],
      url: "https://www.linkedin.com/in/aaryan-rojesara/",
    },
  ],
};

// --- Reusable section wrapper ---
const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`py-10 sm:py-14 md:py-20 relative ${className}`}>
    <div className="container mx-auto px-5 sm:px-6 max-w-6xl">{children}</div>
  </section>
);

const SectionHeading = ({ command }) => (
  <ScrollReveal>
    <div className="mb-8 sm:mb-10 flex items-baseline gap-2 border-b border-black pb-3">
      <span className="font-bold">$</span>
      <span className="text-base sm:text-lg break-all">{command}</span>
    </div>
  </ScrollReveal>
);

// --- Sections ---
const HeroSection = React.memo(({ data, scrollToSection }) => {
  const heroLines = [
    { type: "cmd", text: "whoami" },
    { type: "out", text: data.name },
    { type: "cmd", text: "cat role.txt" },
    { type: "out", text: data.roles.join("  •  ") },
    { type: "cmd", text: "cat about.txt" },
    { type: "out", text: data.bio },
  ];

  return (
    <section
      id="home"
      className="min-h-screen flex items-center pt-24 pb-12 md:pt-24 md:pb-16"
    >
      <div className="container mx-auto px-5 sm:px-6 max-w-6xl">
        <div className="grid md:grid-cols-5 gap-8 md:gap-10 items-center">
          {/* Left: terminal */}
          <motion.div
            className="md:col-span-3"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <TerminalWindow
              title={`${data.user}@${data.host}: ~`}
              bodyClassName="min-h-[230px]"
            >
              <Typewriter lines={heroLines} speed={26} />
            </TerminalWindow>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                className="btn-term"
                onClick={() => scrollToSection("contact")}
              >
                ./contact.sh
              </button>
              <button
                className="btn-term"
                onClick={() => scrollToSection("projects")}
              >
                ls ~/projects
              </button>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              <a
                href={data.contact.github}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:underline underline-offset-4"
              >
                <Github size={16} /> github
              </a>
              <a
                href={data.contact.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:underline underline-offset-4"
              >
                <Linkedin size={16} /> linkedin
              </a>
              <a
                href={`mailto:${data.contact.email}`}
                className="inline-flex items-center gap-2 hover:underline underline-offset-4"
              >
                <Mail size={16} /> email
              </a>
            </div>
          </motion.div>

          {/* Right: black & white profile in a terminal frame */}
          <motion.div
            className="md:col-span-2 md:order-last mx-auto w-full max-w-[260px] sm:max-w-sm"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
          >
            <TerminalWindow title="~/profile.png" bodyClassName="!p-0">
              <img
                src="as+profile.png"
                alt={data.name}
                className="w-full h-auto block bw"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "https://placehold.co/500x500/ffffff/000000?text=Aaryan";
                }}
              />
            </TerminalWindow>
            <p className="mt-3 text-xs text-black/60 text-center">
              {"// based in Ahmedabad, Gujarat, India"}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
});

const ProjectsSection = React.memo(({ projects, onProjectClick }) => {
  const [order, setOrder] = useState("desc");
  const [menuOpen, setMenuOpen] = useState(false);
  const sorted = [...projects].sort((a, b) =>
    order === "desc"
      ? parseInt(b.year, 10) - parseInt(a.year, 10)
      : parseInt(a.year, 10) - parseInt(b.year, 10)
  );
  const options = [
    { v: "desc", label: "year:desc", note: "newest first" },
    { v: "asc", label: "year:asc", note: "oldest first" },
  ];
  return (
    <Section id="projects">
      <ScrollReveal className="relative z-30">
        <div className="mb-8 sm:mb-10 flex items-center gap-2 border-b border-black pb-3 flex-wrap">
          <span className="font-bold">$</span>
          <span className="text-base sm:text-lg">ls ~/projects --sort=</span>
          <div className="relative">
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="border-[1.5px] border-black px-2 py-0.5 text-sm sm:text-base inline-flex items-center gap-1.5 hover:bg-black hover:text-white transition-colors"
            >
              {order === "desc" ? "year:desc" : "year:asc"}
              <ChevronDown
                size={15}
                className={`transition-transform ${menuOpen ? "rotate-180" : ""}`}
              />
            </button>
            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="absolute left-0 top-full mt-1 z-20 bg-white border-[1.5px] border-black shadow-[3px_3px_0_#000] min-w-[200px]">
                  {options.map((opt) => (
                    <button
                      key={opt.v}
                      onClick={() => {
                        setOrder(opt.v);
                        setMenuOpen(false);
                      }}
                      className={`block w-full text-left px-3 py-2 text-sm border-b border-black/10 last:border-b-0 ${
                        order === opt.v
                          ? "bg-black text-white"
                          : "hover:bg-black/5"
                      }`}
                    >
                      --sort={opt.label}{" "}
                      <span
                        className={
                          order === opt.v ? "text-white/60" : "text-black/40"
                        }
                      >
                        # {opt.note}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </ScrollReveal>
      <div className="relative z-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {sorted.map((project, i) => (
        <ScrollReveal key={project.id} delay={(i % 3) * 0.08}>
          <motion.div
            onClick={() => onProjectClick(project)}
            whileHover={{ x: -3, y: -3 }}
            className="group cursor-pointer h-full bg-white border-[1.5px] border-black flex flex-col transition-shadow duration-150 hover:shadow-[6px_6px_0_#000]"
          >
            <div className="relative overflow-hidden border-b-[1.5px] border-black aspect-[16/10]">
              <img
                src={project.image}
                alt={project.title}
                loading="lazy"
                className="w-full h-full object-cover bw group-hover:scale-105 transition-transform duration-500"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://placehold.co/600x400/ffffff/000000?text=${encodeURIComponent(
                    project.title
                  )}`;
                }}
              />
            </div>
            <div className="p-4 flex flex-col flex-grow">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="flex items-center gap-2">
                  <span className="text-black/60">[{project.year}]</span>
                  {project.freelance && (
                    <span className="border border-black px-1.5 leading-tight text-[10px] uppercase tracking-wide">
                      freelance
                    </span>
                  )}
                </span>
                <span className="text-black/40">
                  0{i + 1}/0{projects.length}
                </span>
              </div>
              <h3 className="text-lg font-bold leading-tight">
                {project.title}
              </h3>
              <p className="text-sm text-black/60 mb-3">{project.category}</p>
              <div className="flex flex-wrap gap-1.5 mt-auto">
                {project.tech.slice(0, 3).map((t) => (
                  <span key={t} className="tag">
                    {t}
                  </span>
                ))}
                {project.tech.length > 3 && (
                  <span className="tag">+{project.tech.length - 3}</span>
                )}
              </div>
            </div>
          </motion.div>
          </ScrollReveal>
        ))}
      </div>
    </Section>
  );
});

const ExperienceSection = React.memo(({ experience, education }) => {
  const Row = ({ item, isJob, last }) => (
    <ScrollReveal>
      <div className="relative pl-7 pb-9">
        {/* spine */}
        {!last && (
          <span className="absolute left-[5px] top-3 bottom-0 w-px bg-black/40" />
        )}
        <span className="absolute left-0 top-1.5 w-[11px] h-[11px] border-[1.5px] border-black bg-white" />
        <div className="flex flex-wrap items-baseline gap-x-3">
          <h3 className="text-base sm:text-lg font-bold">
            {isJob ? item.role : item.degree}
          </h3>
          {item.isCurrent && (
            <span className="text-xs border border-black px-2 py-0.5">
              current
            </span>
          )}
        </div>
        <p className="text-sm text-black/70">
          {isJob ? item.company : item.institution}{" "}
          <span className="text-black/40">— {item.period}</span>
        </p>
        <p className="mt-2 text-sm text-black/70 leading-relaxed max-w-2xl">
          {isJob ? item.description : item.detail}
        </p>
        {!isJob && item.sub?.length > 0 && (
          <ul className="mt-3 space-y-1 text-sm text-black/70">
            {item.sub.map((s, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-black/40 select-none">└─</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </ScrollReveal>
  );

  return (
    <Section id="experience">
      <SectionHeading command="cat ~/experience.log" />
      <div className="grid md:grid-cols-2 gap-x-12">
        <div>
          <p className="text-sm text-black/50 mb-6"># work</p>
          {experience.map((item, i) => (
            <Row
              key={i}
              item={item}
              isJob
              last={i === experience.length - 1}
            />
          ))}
        </div>
        <div>
          <p className="text-sm text-black/50 mb-6"># education</p>
          {education.map((item, i) => (
            <Row
              key={i}
              item={item}
              isJob={false}
              last={i === education.length - 1}
            />
          ))}
        </div>
      </div>
    </Section>
  );
});

const AboutSection = React.memo(({ skillGroups }) => (
  <Section id="about">
    <SectionHeading command="cat ~/skills.json" />
    <div className="border-[1.5px] border-black bg-white shadow-[6px_6px_0_#000]">
      <div className="p-5 sm:p-7 space-y-6 text-sm sm:text-base">
        <p className="text-black/50">{"{"}</p>
        {skillGroups.map((group) => (
          <div key={group.title} className="pl-4 sm:pl-6">
            <p className="mb-3">
              <span className="font-bold">"{group.title}"</span>
              <span className="text-black/50">: [</span>
            </p>
            <div className="flex flex-wrap gap-2 pl-4 sm:pl-6">
              {group.skills.map((s) => (
                <span key={s} className="tag">
                  {s}
                </span>
              ))}
            </div>
            <p className="text-black/50 mt-2">],</p>
          </div>
        ))}
        <p className="text-black/50">{"}"}</p>
      </div>
    </div>
  </Section>
));

const BlogSection = React.memo(({ blog, profileUrl }) => (
  <Section id="blog">
    <SectionHeading command="cat ~/blog" />
    <p className="text-sm text-black/50 mb-6"># latest posts & writing — from LinkedIn</p>
    {blog.length === 0 ? (
      <div className="border-[1.5px] border-black p-6 sm:p-8 max-w-2xl">
        <p className="text-sm text-black/70">
          <span className="font-bold">$</span> fetch --source=linkedin
        </p>
        <p className="text-sm text-black/50 mt-2">
          # posts are published on LinkedIn — follow along there for the
          latest.
        </p>
      </div>
    ) : (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {blog.map((post, i) => (
        <ScrollReveal key={i} delay={(i % 3) * 0.08}>
          <a
            href={post.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col h-full bg-white border-[1.5px] border-black p-5 transition-shadow duration-150 hover:shadow-[6px_6px_0_#000]"
          >
            <div className="flex items-center justify-between gap-2 text-xs text-black/50 mb-2">
              <span className="truncate">
                {post.category} · {post.readTime}
              </span>
              <span className="inline-flex items-center gap-1 shrink-0 group-hover:underline underline-offset-4">
                read <ExternalLink size={12} />
              </span>
            </div>
            <h3 className="text-base sm:text-lg font-bold leading-snug mb-2">
              {post.title}
            </h3>
            <p className="text-sm text-black/70 leading-relaxed mb-4">
              {post.blurb}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-auto">
              {post.tags.map((t) => (
                <span key={t} className="tag text-[11px]">
                  #{t}
                </span>
              ))}
            </div>
            </a>
          </ScrollReveal>
        ))}
      </div>
    )}
    <div className="mt-8">
      <a
        href={profileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-term"
      >
        <Linkedin size={16} /> view all posts on LinkedIn
      </a>
    </div>
  </Section>
));

const ContactSection = React.memo(({ contact }) => (
  <Section id="contact">
    <SectionHeading command="./contact.sh" />
    <ScrollReveal>
      <div className="max-w-2xl">
        <p className="text-black/70 mb-1">
          <span className="font-bold">{">"}</span> Open to new opportunities,
          collaborations, and interesting problems.
        </p>
        <p className="text-black/50 mb-6 text-sm">
          {"// reply within 24h"}
        </p>
        <div className="space-y-2 mb-8 text-sm sm:text-base">
          <a
            href={`mailto:${contact.email}`}
            className="flex items-center gap-3 hover:underline underline-offset-4 w-fit"
          >
            <Mail size={18} /> {contact.email}
          </a>
          <a
            href={`tel:${contact.phone.replace(/\s/g, "")}`}
            className="flex items-center gap-3 hover:underline underline-offset-4 w-fit"
          >
            <Phone size={18} /> {contact.phone}
          </a>
        </div>
        <button
          className="btn-term"
          onClick={() => (window.location.href = `mailto:${contact.email}`)}
        >
          <Mail size={16} /> send message
        </button>
      </div>
    </ScrollReveal>
  </Section>
));

// --- Project modal ---
const ProjectModal = ({ project, onClose }) => (
  <AnimatePresence>
    {project && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.96, y: 10 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.96, y: 10 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <TerminalWindow
            title={`~/projects/${project.title
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/(^-|-$)/g, "")}`}
            bodyClassName="!p-0"
          >
            <div className="relative border-b-[1.5px] border-black">
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-56 sm:h-64 object-cover bw"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://placehold.co/800x450/ffffff/000000?text=${encodeURIComponent(
                    project.title
                  )}`;
                }}
              />
              <button
                onClick={onClose}
                className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center border-[1.5px] border-black bg-white hover:bg-black hover:text-white transition-colors"
                aria-label="close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-xs text-black/60">[{project.year}]</p>
                {project.freelance && (
                  <span className="border border-black px-1.5 leading-tight text-[10px] uppercase tracking-wide">
                    freelance
                  </span>
                )}
              </div>
              <h2 className="text-2xl font-bold">{project.title}</h2>
              <p className="text-black/60 mb-5">{project.category}</p>

              <p className="text-sm text-black/50 mb-2"># stack</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {project.tech.map((t) => (
                  <span key={t} className="tag">
                    {t}
                  </span>
                ))}
              </div>

              <p className="text-sm text-black/50 mb-2"># readme</p>
              <div className="text-sm sm:text-base text-black/80 leading-relaxed mb-6 space-y-4">
                <p className="border-l-2 border-black/30 pl-3 text-black/70 italic">
                  {project.description}
                </p>
                <div>
                  <p className="font-bold text-black mb-1">## overview</p>
                  <p>{project.details}</p>
                </div>
                {project.highlights?.length > 0 && (
                  <div>
                    <p className="font-bold text-black mb-1.5">## key features</p>
                    <ul className="space-y-1">
                      {project.highlights.map((h, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="text-black/50 select-none">-</span>
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-3">
                {project.liveUrl !== "#" && (
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-term"
                  >
                    <ExternalLink size={16} /> live demo
                  </a>
                )}
                {project.repoUrl !== "#" && (
                  <a
                    href={project.repoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-term"
                  >
                    <Github size={16} /> source
                  </a>
                )}
              </div>
            </div>
          </TerminalWindow>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// --- Header (mac menu bar) ---
const Header = ({ activeSection, scrollToSection }) => {
  const [open, setOpen] = useState(false);
  const items = [
    { id: "home", label: "home" },
    { id: "projects", label: "projects" },
    { id: "experience", label: "experience" },
    { id: "about", label: "about" },
    { id: "blog", label: "blog" },
    { id: "contact", label: "contact" },
  ];

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white border-b-[1.5px] border-black">
      <div className="container mx-auto px-5 sm:px-6 max-w-6xl flex items-center justify-between h-12">
        <button
          onClick={() => go("home")}
          className="font-bold text-sm flex items-center gap-2"
        >
          <span className="flex gap-1.5">
            <span className="w-[11px] h-[11px] rounded-full border-[1.5px] border-black inline-block" />
            <span className="w-[11px] h-[11px] rounded-full border-[1.5px] border-black inline-block" />
            <span className="w-[11px] h-[11px] rounded-full border-[1.5px] border-black inline-block" />
          </span>
          aaryan@portfolio
        </button>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1 text-sm">
          {items.map((item) => (
            <button
              key={item.id}
              onClick={() => go(item.id)}
              className={`px-3 py-1 transition-colors ${
                activeSection === item.id
                  ? "bg-black text-white"
                  : "hover:underline underline-offset-4"
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden p-1"
          onClick={() => setOpen(!open)}
          aria-label="menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="md:hidden border-t border-black bg-white"
          >
            {items.map((item) => (
              <button
                key={item.id}
                onClick={() => go(item.id)}
                className={`block w-full text-left px-5 py-3 border-b border-black/15 text-sm ${
                  activeSection === item.id
                    ? "bg-black text-white"
                    : "hover:bg-black/5"
                }`}
              >
                ~/{item.label}
              </button>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

// --- Main App ---
export default function App() {
  const [activeSection, setActiveSection] = useState("home");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const scrollToSection = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const openProjectModal = useCallback((project) => {
    setSelectedProject(project);
    document.body.style.overflow = "hidden";
  }, []);

  const closeProjectModal = useCallback(() => {
    setSelectedProject(null);
    document.body.style.overflow = "auto";
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll("section[id]");
      const scrollPos = window.scrollY + window.innerHeight / 3;
      let current = "home";
      for (const section of sections) {
        if (
          section.offsetTop <= scrollPos &&
          section.offsetTop + section.offsetHeight > scrollPos
        ) {
          current = section.id;
          break;
        }
      }
      setActiveSection((prev) => (prev !== current ? current : prev));
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="bg-white text-black min-h-screen relative overflow-x-hidden">
      <Header activeSection={activeSection} scrollToSection={scrollToSection} />

      <main className="relative z-10">
        <HeroSection data={portfolioData} scrollToSection={scrollToSection} />
        <ProjectsSection
          projects={portfolioData.projects}
          onProjectClick={openProjectModal}
        />
        <ExperienceSection
          experience={portfolioData.experience}
          education={portfolioData.education}
        />
        <AboutSection skillGroups={portfolioData.skillGroups} />
        <BlogSection
          blog={portfolioData.blog}
          profileUrl={portfolioData.contact.linkedin}
        />
        <ContactSection contact={portfolioData.contact} />
      </main>

      <footer className="border-t-[1.5px] border-black">
        {/* pixel-dino flourish */}
        <div className="container mx-auto px-5 sm:px-6 max-w-6xl pt-8">
          <div className="relative h-14">
            <div className="absolute bottom-0 inset-x-0 h-px bg-black/40" />
            <PixelDino
              size={48}
              className="absolute bottom-0 left-1 text-black"
            />
            {/* tiny cactus */}
            <svg
              width="26"
              height="40"
              viewBox="0 0 13 20"
              shapeRendering="crispEdges"
              className="absolute bottom-0 left-40 text-black"
              aria-hidden="true"
            >
              <g fill="currentColor">
                <rect x="5" y="2" width="3" height="18" />
                <rect x="1" y="8" width="2" height="6" />
                <rect x="3" y="11" width="2" height="2" />
                <rect x="10" y="6" width="2" height="6" />
                <rect x="8" y="9" width="2" height="2" />
              </g>
            </svg>
          </div>
        </div>
        <div className="container mx-auto px-5 sm:px-6 max-w-6xl pb-7 pt-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
          <p className="text-black/60">
            © {new Date().getFullYear()} {portfolioData.name} — built in the
            terminal
          </p>
          <div className="flex items-center gap-5">
            <a
              href={portfolioData.contact.github}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline underline-offset-4"
            >
              github
            </a>
            <a
              href={portfolioData.contact.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline underline-offset-4"
            >
              linkedin
            </a>
            <a
              href={`mailto:${portfolioData.contact.email}`}
              className="hover:underline underline-offset-4"
            >
              email
            </a>
          </div>
        </div>
      </footer>

      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            onClick={() => scrollToSection("home")}
            className="fixed bottom-6 right-6 w-11 h-11 flex items-center justify-center border-[1.5px] border-black bg-white hover:bg-black hover:text-white transition-colors z-50 shadow-[3px_3px_0_#000]"
            aria-label="scroll to top"
          >
            <ArrowUp size={20} />
          </motion.button>
        )}
      </AnimatePresence>

      <ProjectModal project={selectedProject} onClose={closeProjectModal} />
    </div>
  );
}
